import type { Lang } from '../types';

export interface CityTaxReportText {
  backToApartment: string;
  pageTitle: string;
  subtitle: string;
  labelFrom: string;
  labelTo: string;
  calculating: string;
  calculate: string;
  cardTotalTax: string;
  cardReservations: string;
  cardAdults: (rate: string) => string;
  cardChildren: (rate: string) => string;
  cardDisabled: (rate: string) => string;
  guestsNights: (guests: number, nights: number) => string;
  breakdownLine: (adults: string, children: string, disabled: string) => string;
  noReservations: string;
  thReservationId: string;
  thGuest: string;
  thCheckIn: string;
  thCheckOut: string;
  thNights: string;
  thAdults: string;
  thChildren: string;
  thDisabled: string;
  thTax: string;
  total: string;
  labelMonth: string;
  labelDistrict: string;
  downloadPdf: string;
  pdfLoading: string;
  pdfNoContact: string;
  pdfNoMailbox: string;
  partialBadge: string;
  partialTooltip: (counted: number, total: number) => string;
  /** Basis toggle (#206): which complete guests the report counts. */
  basisLabel: string;
  basisCompleted: string;
  basisFiled: string;
  /** Shown when the filed basis left guests out. "Excluded" is not "exempt" - the wording must say so. */
  excludedBox: (n: number) => string;
  excludedNone: string;
  excludedLine: (n: number) => string;
  excludedTooltip: string;
}

export const cityTaxReportText: Record<Lang, CityTaxReportText> = {
  cs: {
    backToApartment: '← Zpět na apartmán',
    pageTitle: 'Přehled místního poplatku',
    subtitle: 'Vypočítejte vybraný místní poplatek za zvolené období (dle data příjezdu).',
    labelFrom: 'Od',
    labelTo: 'Do',
    calculating: 'Počítám…',
    calculate: 'Vypočítat',
    cardTotalTax: 'Celkem poplatek',
    cardReservations: 'Rezervace',
    cardAdults: (rate) => `Dospělí (${rate} Kč/noc)`,
    cardChildren: (rate) => `Děti (${rate} Kč/noc)`,
    cardDisabled: (rate) => `ZTP (${rate} Kč/noc)`,
    guestsNights: (guests, nights) => `${guests} hostů · ${nights} nocí`,
    breakdownLine: (adults, children, disabled) => `Dospělí ${adults} · Děti ${children} · ZTP ${disabled} Kč`,
    noReservations: 'V tomto období nebyly nalezeny žádné rezervace s vyplněnými formuláři hostů.',
    thReservationId: 'ID',
    thGuest: 'Host',
    thCheckIn: 'Příjezd',
    thCheckOut: 'Odjezd',
    thNights: 'Nocí',
    thAdults: 'Dospělí',
    thChildren: 'Děti',
    thDisabled: 'ZTP',
    thTax: 'Poplatek (Kč)',
    total: 'Celkem',
    labelMonth: 'Měsíc',
    labelDistrict: 'Městská část Praha',
    downloadPdf: 'Stáhnout PDF',
    pdfLoading: 'Generuji PDF…',
    pdfNoContact: 'Přiřaďte kontakt v záložce Místní poplatek pro generování PDF.',
    pdfNoMailbox: 'Přiřaďte datovou schránku příjemce v záložce Místní poplatek.',
    partialBadge: 'částečně',
    partialTooltip: (counted, total) => `Pobyt trvá ${total} nocí celkem, do tohoto měsíce spadá pouze ${counted}. Zbytek je v sousedním měsíci.`,
    basisLabel: 'Počítat',
    basisCompleted: 'Všichni hosté, kteří dokončili check-in',
    basisFiled: 'Jen hosté nahlášení do Ubyportu (+ čeští hosté)',
    excludedBox: (n) => `Nezahrnuto hostů: ${n} – zahraniční hosté zatím nenahlášení do Ubyportu. To, že host nebyl nahlášen, neznamená, že je osvobozen od místního poplatku; před podáním hlášení to ověřte.`,
    excludedNone: 'Žádný host není vynechán – všichni zahraniční hosté jsou nahlášení do Ubyportu.',
    excludedLine: (n) => `−${n} nenahlášeno`,
    excludedTooltip: 'Zahraniční host zatím nenahlášený do Ubyportu – v tomto přehledu se nezapočítává (nejde o osvobození od poplatku).',
  },
  en: {
    backToApartment: '← Back to Apartment',
    pageTitle: 'City Tax Report',
    subtitle: 'Calculate collected city tax for a date range (by check-in date).',
    labelFrom: 'From',
    labelTo: 'To',
    calculating: 'Calculating…',
    calculate: 'Calculate',
    cardTotalTax: 'Total Tax',
    cardReservations: 'Reservations',
    cardAdults: (rate) => `Adults (${rate} Kč/night)`,
    cardChildren: (rate) => `Children (${rate} Kč/night)`,
    cardDisabled: (rate) => `Disabled (${rate} Kč/night)`,
    guestsNights: (guests, nights) => `${guests} guests · ${nights} nights`,
    breakdownLine: (adults, children, disabled) => `Adults ${adults} · Children ${children} · Disabled ${disabled} Kč`,
    noReservations: 'No reservations with completed guest forms found in this range.',
    thReservationId: 'ID',
    thGuest: 'Guest',
    thCheckIn: 'Check-in',
    thCheckOut: 'Check-out',
    thNights: 'Nights',
    thAdults: 'Adults',
    thChildren: 'Children',
    thDisabled: 'Disabled',
    thTax: 'Tax (Kč)',
    total: 'Total',
    labelMonth: 'Month',
    labelDistrict: 'Praha District',
    downloadPdf: 'Download PDF',
    pdfLoading: 'Generating PDF…',
    pdfNoContact: 'Assign a contact in the City Tax tab to enable PDF generation.',
    pdfNoMailbox: 'Assign a recipient mailbox in the City Tax tab.',
    partialBadge: 'partial',
    partialTooltip: (counted, total) => `Stay is ${total} nights total, only ${counted} fall in this month. The rest is counted in the neighboring month's report.`,
    basisLabel: 'Count',
    basisCompleted: 'All guests who completed check-in',
    basisFiled: 'Only guests filed to Ubyport (+ Czech guests)',
    excludedBox: (n) => `${n} guest${n === 1 ? '' : 's'} excluded — foreign guests not (yet) filed to Ubyport. Not being filed does not mean the guest is exempt from the city tax; check before declaring.`,
    excludedNone: 'No guests excluded — every foreign guest is filed to Ubyport.',
    excludedLine: (n) => `−${n} not filed`,
    excludedTooltip: 'Foreign guest not (yet) filed to Ubyport — left out of this report (this is not a tax exemption).',
  },
  vi: {
    backToApartment: '← Quay lại căn hộ',
    pageTitle: 'Báo cáo thuế thành phố',
    subtitle: 'Tính thuế thành phố thu được trong khoảng thời gian (theo ngày nhận phòng).',
    labelFrom: 'Từ ngày',
    labelTo: 'Đến ngày',
    calculating: 'Đang tính…',
    calculate: 'Tính toán',
    cardTotalTax: 'Tổng thuế',
    cardReservations: 'Lịch đặt',
    cardAdults: (rate) => `Người lớn (${rate} Kč/đêm)`,
    cardChildren: (rate) => `Trẻ em (${rate} Kč/đêm)`,
    cardDisabled: (rate) => `Người khuyết tật (${rate} Kč/đêm)`,
    guestsNights: (guests, nights) => `${guests} khách · ${nights} đêm`,
    breakdownLine: (adults, children, disabled) => `Người lớn ${adults} · Trẻ em ${children} · Khuyết tật ${disabled} Kč`,
    noReservations: 'Không tìm thấy lịch đặt có biểu mẫu hoàn chỉnh trong khoảng thời gian này.',
    thReservationId: 'ID',
    thGuest: 'Khách',
    thCheckIn: 'Nhận phòng',
    thCheckOut: 'Trả phòng',
    thNights: 'Đêm',
    thAdults: 'Người lớn',
    thChildren: 'Trẻ em',
    thDisabled: 'Khuyết tật',
    thTax: 'Thuế (Kč)',
    total: 'Tổng cộng',
    labelMonth: 'Tháng',
    labelDistrict: 'Quận Praha',
    downloadPdf: 'Tải PDF',
    pdfLoading: 'Đang tạo PDF…',
    pdfNoContact: 'Gán liên hệ trong tab Thuế thành phố để tạo PDF.',
    pdfNoMailbox: 'Gán hộp thư người nhận trong tab Thuế thành phố.',
    partialBadge: 'một phần',
    partialTooltip: (counted, total) => `Lượt ở tổng cộng ${total} đêm, chỉ ${counted} đêm thuộc tháng này. Phần còn lại tính vào báo cáo của tháng lân cận.`,
    basisLabel: 'Tính theo',
    basisCompleted: 'Tất cả khách đã hoàn tất check-in',
    basisFiled: 'Chỉ khách đã khai báo lên Ubyport (+ khách Séc)',
    excludedBox: (n) => `Đã loại ${n} khách — khách nước ngoài chưa được khai báo lên Ubyport. Chưa khai báo không có nghĩa là khách được miễn thuế thành phố; hãy kiểm tra trước khi nộp tờ khai.`,
    excludedNone: 'Không loại khách nào — mọi khách nước ngoài đều đã được khai báo lên Ubyport.',
    excludedLine: (n) => `−${n} chưa khai báo`,
    excludedTooltip: 'Khách nước ngoài chưa được khai báo lên Ubyport — không được tính trong báo cáo này (không phải là miễn thuế).',
  },
  uk: {
    backToApartment: '← Назад до апартаменту',
    pageTitle: 'Звіт з туристичного збору',
    subtitle: 'Розрахуйте зібраний туристичний збір за вибраний період (за датою заїзду).',
    labelFrom: 'Від',
    labelTo: 'До',
    calculating: 'Розраховую…',
    calculate: 'Розрахувати',
    cardTotalTax: 'Загальний збір',
    cardReservations: 'Бронювань',
    cardAdults: (rate) => `Дорослі (${rate} Kč/ніч)`,
    cardChildren: (rate) => `Діти (${rate} Kč/ніч)`,
    cardDisabled: (rate) => `З інвалідністю (${rate} Kč/ніч)`,
    guestsNights: (guests, nights) => `${guests} гостей · ${nights} ночей`,
    breakdownLine: (adults, children, disabled) => `Дорослі ${adults} · Діти ${children} · З інвалідністю ${disabled} Kč`,
    noReservations: 'У цьому діапазоні не знайдено бронювань із заповненими формами гостей.',
    thReservationId: 'ID',
    thGuest: 'Гість',
    thCheckIn: 'Заїзд',
    thCheckOut: 'Виїзд',
    thNights: 'Ночей',
    thAdults: 'Дорослі',
    thChildren: 'Діти',
    thDisabled: 'З інвалідністю',
    thTax: 'Збір (Kč)',
    total: 'Разом',
    labelMonth: 'Місяць',
    labelDistrict: 'Район Праги',
    downloadPdf: 'Завантажити PDF',
    pdfLoading: 'Генерую PDF…',
    pdfNoContact: 'Призначте контакт на вкладці Туристичний збір для генерації PDF.',
    pdfNoMailbox: 'Призначте поштову скриньку одержувача на вкладці Туристичний збір.',
    partialBadge: 'частково',
    partialTooltip: (counted, total) => `Проживання триває ${total} ночей загалом, у цей місяць входить лише ${counted}. Решта врахована у звіті сусіднього місяця.`,
    basisLabel: 'Рахувати',
    basisCompleted: 'Усіх гостей, які завершили реєстрацію',
    basisFiled: 'Лише гостей, поданих до Ubyport (+ чеських гостей)',
    excludedBox: (n) => `Виключено гостей: ${n} — іноземні гості ще не подані до Ubyport. Те, що гостя не подано, не означає, що він звільнений від туристичного збору; перевірте перед поданням декларації.`,
    excludedNone: 'Нікого не виключено — усі іноземні гості подані до Ubyport.',
    excludedLine: (n) => `−${n} не подано`,
    excludedTooltip: 'Іноземного гостя ще не подано до Ubyport — у цьому звіті не враховано (це не звільнення від збору).',
  },
};
